import React, { useEffect, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Tables } from "../../utils/database.types";
import { mkConfig, generateCsv, download } from "../export-to-csv/lib";
import { Link } from "react-router-dom";
import useSupabaseBrowser from "../hooks/use-supabase-browser";
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { getParkings } from "../api/get-parkings";
import dayjs from "dayjs";
import * as timezone from "dayjs/plugin/timezone";
import * as utc from "dayjs/plugin/utc";
import { useSubscription } from "../hooks/use-subscription";
import { REALTIME_POSTGRES_CHANGES_LISTEN_EVENT } from "@supabase/supabase-js";

dayjs.extend(timezone);
dayjs.extend(utc);
const csvConfig = mkConfig({
  useKeysAsHeaders: true,
  filename: `parkings-${dayjs().format("YYYYMMDDHHMM")}.csv`,
});

type RowData = Tables<"parkings"> & {
  venue: { id: string; name: string } | null;
  user: { id: string; full_name: string } | null;
  parking_events: { type: string; timestamp: string }[];
};
export default () => {
  const [filteredData, setFilteredData] = useState<RowData[]>([]);
  const [limit, setLimit] = useState(100);
  const supabase = useSupabaseBrowser();
  const {
    data: parkings = [],
    isLoading,
    isError,
  } = useQuery(getParkings(supabase, limit));

  const utcToOrganizationTimezoneFromatted = (utcTime: string | null) => {
    if (!utcTime) return;
    return dayjs(utcTime).tz("America/New_York").format("hh:mm A MMMM D, YYYY");
  };
  const getTransitionTimestampFromEvents = (events) => {
    if (!events) return {};
    return {
      requested_at: events.find((e) => e.type === "request")?.timestamp,
      retrieving_at: events.find((e) => e.type === "retrieve")?.timestamp,
      delivered_at: events.find((e) => e.type === "deliver")?.timestamp,
    };
  };
  const handleParkingsUpdates = async (payload: any, event: string) => {
    if (event === REALTIME_POSTGRES_CHANGES_LISTEN_EVENT.DELETE) {
      return;
    } else {
      const { data: newParking } = await supabase
        .from("parkings")
        .select(
          `id, make(name), model, color, ticket, venue_id, created_by:users(id, full_name), status, created_at, venue:venues(name, id), parking_events(timestamp, type, user_id), paid, payment_method`
        )
        .eq("id", payload.new.id)
        .single();
      setFilteredData((data) =>
        data
          .filter((e) => e.id !== payload.new.id)
          .concat(newParking)
          .sort(
            (a: RowData, b: RowData) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          )
      );
    }
  };
  useSubscription(handleParkingsUpdates, "realtimeParkings", "parkings");

  const columnHelper = createColumnHelper<RowData>();
  const columns = [
    columnHelper.accessor("ticket", {
      header: "Ticket",
      cell: (info) => info.getValue(),
      footer: (info) => info.column.id,
    }),
    columnHelper.display({
      id: "description",
      header: "Description",
      cell: ({ row }) =>
        [row.original.make?.name, row.original.model, row.original.color].join(
          " "
        ),
    }),
    columnHelper.accessor("venue", {
      header: "Venue",
      cell: (info) => {
        const venue = info.getValue();

        if (!venue) {
          return "-";
        }
        return <Link to={`/venues/${venue.id}/edit`}>{venue.name}</Link>;
      },
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("created_by", {
      header: "Parked by",
      cell: (info) => {
        const valet = info.getValue();

        if (!valet) {
          return "-";
        }
        return <Link to={`/valets/${valet.id}/edit`}>{valet.full_name}</Link>;
      },
      footer: (info) => info.column.id,
    }),

    columnHelper.accessor("status", {
      header: "Status",
      cell: (info) => info.getValue(),
      footer: (info) => info.column.id,
    }),

    columnHelper.display({
      id: "request_method",
      cell: ({ row }) => {
        const req = row.original.parking_events
          .slice()
          .reverse()
          .find((e) => e.type === "request");
        if (!req) return "";
        if (req.user_id === null) return "SMS";
        if (req.user_id !== null) return "Paper";
      },
      header: "Request Method",
    }),
    columnHelper.display({
      id: "payment_method",
      cell: ({ row }) => {
        if (row.original.paid) return row.original.payment_method || "Link";
      },
      header: "Payment Method",
    }),

    columnHelper.accessor("created_at", {
      header: "Parked",
      cell: (info) => utcToOrganizationTimezoneFromatted(info.getValue()),
      footer: (info) => info.column.id,
    }),
    columnHelper.display({
      id: "requested_at",
      header: "Requested",
      cell: ({ row }) =>
        utcToOrganizationTimezoneFromatted(
          getTransitionTimestampFromEvents(
            row.original.parking_events.slice().reverse()
          )["requested_at"]
        ),
    }),
    columnHelper.display({
      id: "delivered_at",
      header: "Delivered",
      cell: ({ row }) =>
        utcToOrganizationTimezoneFromatted(
          getTransitionTimestampFromEvents(
            row.original.parking_events.slice().reverse()
          )["delivered_at"]
        ),
    }),
  ];

  useEffect(() => {
    if (!parkings) return;
    setFilteredData(parkings);
  }, [parkings]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const exportToCsv = () => {
    const data = table.getRowModel().rows.map((row) => {
      return Object.fromEntries(
        row.getVisibleCells().map((cell) => {
          if (cell.column.id === "venue")
            return ["venue", cell.getValue().name];
          if (cell.column.id === "created_by")
            return ["created_by", cell.getValue().full_name];

          return [
            cell.column.id,
            cell.column.columnDef.cell(cell.getContext()),
          ];
        })
      );
    });
    const csv = generateCsv(csvConfig)(data);
    download(csvConfig)(csv);
  };

  if (isLoading) {
    return <span className="loading loading-dots loading-lg"></span>;
  }

  if (isError || !parkings) {
    return <div>Error</div>;
  }

  return (
    <section className="card overflow-hidden bg-base-100 shadow-sm">
      <div className="flex p-4 self-end">
        <select
          className="select"
          defaultValue={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
        >
          {[50, 100, 500, 1000, 5000].map((l) => (
            <option key={l} value={l}>
              Show last {l}
            </option>
          ))}
        </select>
        <button className="btn btn-success" onClick={exportToCsv}>
          Export to CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="table table-zebra">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder ? null : (
                        <div>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
